import { useCallback, useEffect, useState } from 'react';

import { Box, Divider, ListItemText, Typography } from '@mui/material';

import { EnumModel } from '../../../api/api';
import Spinner from '../../../common/components/Spinner';
import CopyToClipboardButton from '../../../components/Clipboard/CopyToClipboardButton';
import { useEnumMutations } from '../Hooks/useEnumController';
import EnumItemTable from './EnumItemTable';

interface Props {
	enumModel: EnumModel;
}

const EnumSection: React.FC<Props> = ({ enumModel }) => {
	const [enumDetails, setEnumDetails] = useState<EnumModel | null>(null);
	const [isWorking, setIsWorking] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const { getEnumById, addEnumItem } = useEnumMutations(enumModel?.contextId);

	const getEnumDetails = useCallback(async () => {
		setIsLoading(true);
		try {
			const details = await getEnumById(enumModel?.id);
			setEnumDetails(details!);
		} catch (error) {
			console.error('Failed to fetch enum details:', error);
		} finally {
			setIsLoading(false);
		}
	}, [getEnumById, enumModel?.id]);

	const handleAddEnumItem = async (itemName: string) => {
		setIsWorking(true);
		try {
			await addEnumItem(enumModel?.name ?? '', itemName);
		} catch (error) {
			console.error('Failed to add enum item:', error);
		} finally {
			setIsWorking(false);
		}
		await getEnumDetails();
	};

	// Reset state when enum model changes
	useEffect(() => {
		setEnumDetails(null);
	}, [enumModel?.id]);

	useEffect(() => {
		if (enumDetails) {
			return;
		}

		const loadDetails = async () => {
			await getEnumDetails();
		};

		void loadDetails();
	}, [enumDetails, getEnumDetails]);

	return (
		<>
			{(isLoading || isWorking) && <Spinner />}
			<Box sx={{ mb: 4 }}>
				<Box
					sx={{
						borderRadius: 1,
						backgroundColor: 'background.paper',
						pl: 2,
						py: 1,
						display: 'flex',
						alignItems: 'center',
						'&:hover': {
							backgroundColor: 'rgba(255, 255, 255, 0.05)',
						},
					}}
				>
					<ListItemText
						primary={<Typography variant="h6">{enumModel?.name}</Typography>}
						secondary={<Typography variant="body2">{enumModel?.description}</Typography>}
					/>
					<CopyToClipboardButton value={enumModel?.id} size="small" title="Copy enum ID to clipboard" iconSize={18} edge={false} />
				</Box>

				<EnumItemTable onAddEnumItem={handleAddEnumItem} enumName={enumDetails?.name ?? ''} items={enumDetails?.items ?? []} />

				<Divider sx={{ mt: 3 }} />
			</Box>
		</>
	);
};

export default EnumSection;
